import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, ShoppingBag, X } from "lucide-react";
import { useCart } from "../../../auth/CartContext";
import { useDepartments } from "../../../lib/queries";
import { fetchMatchingJewellery, imgSrc } from "../pdp/completeTheLookMatch";
import RecoCard from "../pdp/RecoCard";

/**
 * AddedCrossSellModal — a single, globally-mounted "Added to your bag"
 * confirmation + jewellery cross-sell.
 *
 * Mounted ONCE in ShopLayout. Listens for the `jns:item-added` CustomEvent
 * dispatched by CartContext.add() on every add. It OPENS only when BOTH:
 *   1. the added item is NOT jewellery (department_slug !== "jewellery"),
 *      so a jewellery add can never re-open the modal (no loop), and
 *   2. the jewellery department is LIVE.
 * Otherwise it does nothing — the existing sonner toast remains the only
 * feedback (this modal never suppresses it).
 *
 * Match logic is shared verbatim with the PDP rail via
 * `fetchMatchingJewellery` so the two surfaces can never drift.
 *
 * Layout: mobile = bottom-sheet, desktop = centered dialog.
 */
export default function AddedCrossSellModal() {
    const { add } = useCart();
    const navigate = useNavigate();
    const location = useLocation();
    const { data: departments = [] } = useDepartments();
    const jewelleryLive = departments.some(
        (d) => d.slug === "jewellery" && d.status === "live"
    );

    const [open, setOpen] = useState(false);
    const [addedItem, setAddedItem] = useState(null);   // the clothing line just added
    const [jewels, setJewels] = useState([]);           // matched jewellery to cross-sell
    const [added, setAdded] = useState({});             // jewel.id → true (tick confirm)
    const timers = useRef({});                          // per-jewel confirm timeouts

    // Desktop scroll affordance for the cross-sell rail (mobile swipes natively,
    // but desktop has no touch + the scrollbar is hidden — so surface arrows).
    const railRef = useRef(null);
    const [canL, setCanL] = useState(false);
    const [canR, setCanR] = useState(false);
    const recalcRail = useCallback(() => {
        const el = railRef.current;
        if (!el) return;
        const max = el.scrollWidth - el.clientWidth;
        setCanL(el.scrollLeft > 4);
        setCanR(el.scrollLeft < max - 4);
    }, []);
    const scrollRail = (dir) => {
        const el = railRef.current;
        if (el) el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: "smooth" });
    };

    const close = useCallback(() => {
        setOpen(false);
    }, []);

    // Close on any route change — tapping a cross-sell card opens that item's
    // PDP, and this globally-mounted modal must not linger over the new page.
    // (Opening is driven by add-to-cart, which never changes the route, so this
    // can never fight the open.)
    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    // Keep the latest jewelleryLive flag available to the event handler
    // without re-subscribing the listener on every departments refetch.
    const jewelleryLiveRef = useRef(jewelleryLive);
    useEffect(() => { jewelleryLiveRef.current = jewelleryLive; }, [jewelleryLive]);

    // Subscribe once to the global add event.
    useEffect(() => {
        let alive = true;
        const onAdded = (e) => {
            const item = e?.detail?.item;
            if (!item) return;
            // Never open for a jewellery add (no loop), and only when the
            // jewellery vertical is live.
            if (item.department_slug === "jewellery") return;
            if (!jewelleryLiveRef.current) return;

            fetchMatchingJewellery(item, { excludeId: item.id }).then((matched) => {
                if (!alive) return;
                // Never show an empty rail — only open when we actually
                // have jewellery to pair.
                if (!matched || matched.length === 0) return;
                setAddedItem(item);
                setJewels(matched.slice(0, 6));
                setAdded({});
                setOpen(true);
            });
        };
        window.addEventListener("jns:item-added", onAdded);
        return () => {
            alive = false;
            window.removeEventListener("jns:item-added", onAdded);
        };
    }, []);

    // Esc to close + body-scroll lock while open.
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === "Escape") close(); };
        window.addEventListener("keydown", onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, close]);

    // Clean up any pending confirm timers on unmount.
    useEffect(() => () => {
        Object.values(timers.current).forEach((t) => clearTimeout(t));
    }, []);

    // Track scroll edges so the desktop arrows enable/disable + auto-hide.
    useEffect(() => {
        if (!open) return undefined;
        const el = railRef.current;
        if (!el) return undefined;
        recalcRail();
        const onScroll = () => recalcRail();
        el.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", recalcRail);
        return () => {
            el.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", recalcRail);
        };
    }, [open, jewels, recalcRail]);

    const handleAddJewel = (e, jewel) => {
        // RecoCard is a <Link> — stop the tap from navigating to the jewel PDP.
        if (e) { e.preventDefault(); e.stopPropagation(); }
        // Match CompleteTheLook's add signature exactly. This dispatches
        // another jns:item-added, but department_slug === "jewellery" so the
        // listener short-circuits — the modal does NOT re-open or close.
        add(jewel, 1, { size: null });
        setAdded((prev) => ({ ...prev, [jewel.id]: true }));
        if (timers.current[jewel.id]) clearTimeout(timers.current[jewel.id]);
        timers.current[jewel.id] = setTimeout(() => {
            setAdded((prev) => ({ ...prev, [jewel.id]: false }));
        }, 1600);
    };

    const goToBag = () => { close(); navigate("/cart"); };

    if (!open || !addedItem) return null;

    const addedImg = imgSrc(
        addedItem.photo_url ||
        (addedItem.photo_urls && addedItem.photo_urls[0]) ||
        null
    );

    return (
        <div
            className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center"
            data-testid="added-crosssell-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Added to your bag"
        >
            {/* Overlay */}
            <div
                className="absolute inset-0 bg-ink/50 backdrop-blur-[2px] animate-[fadeIn_.35s_cubic-bezier(0.16,1,0.3,1)]"
                onClick={close}
                data-testid="added-crosssell-overlay"
            />

            {/* Sheet / dialog */}
            <div
                className="relative w-full sm:w-[min(560px,92vw)] max-h-[92vh] sm:max-h-[86vh]
                           flex flex-col overflow-hidden bg-ivory
                           border border-rosegold/30
                           rounded-t-3xl sm:rounded-2xl
                           shadow-[0_-24px_70px_-30px_rgba(80,30,30,0.45)] sm:shadow-[0_30px_80px_-30px_rgba(80,30,30,0.4)]
                           animate-[addedSheetIn_.5s_cubic-bezier(0.16,1,0.3,1)]"
                style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
            >
                {/* Grab handle (mobile only) */}
                <div className="sm:hidden pt-2.5 flex justify-center flex-shrink-0">
                    <span className="block w-10 h-1 rounded-full bg-rosegold/40" />
                </div>

                {/* Close */}
                <button
                    type="button"
                    onClick={close}
                    data-testid="added-crosssell-close"
                    aria-label="Close"
                    className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full flex items-center justify-center
                               text-ink-soft hover:text-ink hover:bg-ivory-soft transition-colors"
                >
                    <X size={18} strokeWidth={1.5} />
                </button>

                <div className="overflow-y-auto no-scrollbar px-5 sm:px-7 pt-4 sm:pt-6 pb-5 sm:pb-6">
                    {/* Confirmation header */}
                    <div className="flex items-center gap-2.5 pr-8">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-600/10 text-emerald-700 flex-shrink-0">
                            <Check size={16} strokeWidth={2.5} />
                        </span>
                        <h2 className="font-heading text-2xl sm:text-3xl text-ink leading-none">
                            Added to your bag <span className="text-emerald-700">✓</span>
                        </h2>
                    </div>

                    {/* Added item line */}
                    <div className="mt-4 flex items-center gap-3 rounded-2xl border border-rosegold/25 bg-white/70 p-2.5 sm:p-3">
                        <div className="w-14 h-16 sm:w-16 sm:h-20 rounded-xl overflow-hidden bg-ivory-soft flex-shrink-0">
                            <img
                                src={addedImg}
                                alt={addedItem.name}
                                className="w-full h-full object-cover object-top"
                                onError={(e) => { e.target.src = "/default-item.svg"; }}
                            />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-ink line-clamp-2 leading-snug">
                                {addedItem.name}
                            </p>
                            <p className="mt-1 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.14em] text-emerald-700 font-bold">
                                <Check size={12} strokeWidth={2.5} /> Added
                            </p>
                        </div>
                    </div>

                    {/* Complete the look */}
                    <div className="mt-6">
                        <p className="text-[10px] uppercase tracking-[0.28em] text-rosegold font-bold mb-1.5 flex items-center gap-2.5">
                            <span className="inline-block w-6 h-px bg-rosegold" />
                            Complete the look
                        </p>
                        <h3 className="font-heading text-xl sm:text-2xl text-ink leading-tight">
                            Pair it with <span className="italic text-wine">jewellery</span>.
                        </h3>
                        <p className="mt-1 text-[12px] text-ink-soft">
                            Hand-picked pieces that finish this look — add them in a tap.
                        </p>

                        <div className="relative mt-4">
                            {/* Desktop scroll arrows — mobile swipes natively, but
                                desktop has no touch + a hidden scrollbar, so the
                                extra cards were unreachable. Auto-hide at each edge. */}
                            <button
                                type="button"
                                onClick={() => scrollRail(-1)}
                                disabled={!canL}
                                aria-label="Scroll to previous pieces"
                                data-testid="added-crosssell-prev"
                                className="hidden sm:flex absolute -left-2 top-[38%] -translate-y-1/2 z-10 w-9 h-9 items-center justify-center rounded-full bg-white border border-rosegold/30 text-ink shadow-[0_6px_18px_-6px_rgba(80,30,30,0.4)] hover:bg-rosegold hover:text-ivory hover:border-rosegold transition-all disabled:opacity-0 disabled:pointer-events-none"
                            >
                                <ChevronLeft size={18} strokeWidth={1.75} />
                            </button>
                            <button
                                type="button"
                                onClick={() => scrollRail(1)}
                                disabled={!canR}
                                aria-label="Scroll to more pieces"
                                data-testid="added-crosssell-next"
                                className="hidden sm:flex absolute -right-2 top-[38%] -translate-y-1/2 z-10 w-9 h-9 items-center justify-center rounded-full bg-white border border-rosegold/30 text-ink shadow-[0_6px_18px_-6px_rgba(80,30,30,0.4)] hover:bg-rosegold hover:text-ivory hover:border-rosegold transition-all disabled:opacity-0 disabled:pointer-events-none"
                            >
                                <ChevronRight size={18} strokeWidth={1.75} />
                            </button>
                            {/* Soft right-edge fade signals "more →" while scrollable. */}
                            <div className={`pointer-events-none absolute right-0 top-0 bottom-1 w-12 bg-gradient-to-l from-ivory via-ivory/60 to-transparent z-[1] transition-opacity duration-300 ${canR ? "opacity-100" : "opacity-0"}`} />

                            <div
                                ref={railRef}
                                className="flex gap-3 overflow-x-auto no-scrollbar snap-x snap-mandatory -mx-1 px-1 pb-1"
                                data-testid="added-crosssell-scroll"
                            >
                                {jewels.map((p) => (
                                    <RecoCard
                                        key={p.id}
                                        item={p}
                                        testid={`added-crosssell-card-${p.id}`}
                                        width="w-[43%] sm:w-[38%]"
                                        onAdd={handleAddJewel}
                                        added={!!added[p.id]}
                                        addTestid={`added-crosssell-add-${p.id}`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Sticky footer CTAs */}
                <div className="flex-shrink-0 border-t border-rosegold/20 bg-ivory/95 backdrop-blur-sm px-5 sm:px-7 py-3.5 flex items-center gap-3"
                     style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 0.875rem)" }}>
                    <button
                        type="button"
                        onClick={close}
                        data-testid="added-crosssell-continue"
                        className="flex-1 h-11 rounded-full border border-rosegold/40 bg-ivory text-ink text-sm font-medium
                                   hover:border-rosegold hover:bg-ivory-soft transition-colors duration-300"
                    >
                        Continue shopping
                    </button>
                    <button
                        type="button"
                        onClick={goToBag}
                        data-testid="added-crosssell-view-bag"
                        className="flex-1 h-11 rounded-full bg-wine hover:bg-wine-hover text-ivory text-sm font-bold
                                   inline-flex items-center justify-center gap-2 transition-colors duration-300
                                   shadow-[0_14px_30px_-16px_rgba(107,44,42,0.6)]"
                    >
                        <ShoppingBag size={16} strokeWidth={1.75} /> View bag
                    </button>
                </div>
            </div>
        </div>
    );
}
